import PermissionButton from "../../components/ui/PermissionButton";
import { MENU_UNITS } from "../../utils/units";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../Layouts/DashboardLayout";

const API = "/api";

const UOMS = MENU_UNITS;

const DEFAULT_ITEM = {
  item_code: "",
  name: "",
  veg_type: "Veg",
  department_id: "",
  category_id: "",
  uom: "PCS",
  description: "",
  active: true,
  fast_moving: false,
  inventory_item_id: "",
  rates: [],
  taxes: [
    { name: "CGST", percent: 2.5 },
    { name: "SGST", percent: 2.5 },
  ],
};

export default function MenuMasterAdd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [tab, setTab] = useState("basic");
  const [item, setItem] = useState(DEFAULT_ITEM);
  const [loadingItem, setLoadingItem] = useState(isEdit);
  const [savingItem, setSavingItem] = useState(false);

  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);

  const [metaModal, setMetaModal] = useState(null);
  const [metaName, setMetaName] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  const fetchBranches = async () => {
    const res = await fetch(`${API}/branches`, {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setBranches(data.data);
  };

  const fetchInventory = async () => {
    const res = await fetch(`${API}/inventory/items`, {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") {
      setInventoryItems(
        (data.data || []).filter((inventoryItem) =>
          ["finished", "finished good", "finished goods"].includes(
            String(inventoryItem.type || "").trim().toLowerCase(),
          ),
        ),
      );
    }
  };

  const fetchDepartments = async () => {
    const res = await fetch(`${API}/menumeta/departments`, {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setDepartments(data.data);
  };

  const fetchCategories = async () => {
    const res = await fetch(`${API}/menumeta/categories`, {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setCategories(data.data);
  };

  useEffect(() => {
    fetchBranches();
    fetchInventory();
    fetchDepartments();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const fetchMenuItem = async () => {
      try {
        const res = await fetch(`${API}/menu/${id}`, {
          credentials: "include",
        });

        const data = await res.json();

        if (data.status !== "success") {
          alert(data.message || "Menu item not found");
          navigate("/admindashboard/menu-master");
          return;
        }

        setItem({
          ...DEFAULT_ITEM,
          ...data.data,
          inventory_item_id: data.data.inventory_item_id || "",
          department_id: data.data.department_id || "",
          category_id: data.data.category_id || "",
          rates: data.data.rates || [],
          taxes: data.data.taxes?.length ? data.data.taxes : DEFAULT_ITEM.taxes,
        });
      } catch (error) {
        console.error(error);
        alert("Could not load menu item");
        navigate("/admindashboard/menu-master");
      } finally {
        setLoadingItem(false);
      }
    };

    fetchMenuItem();
  }, [id, isEdit, navigate]);

  const saveMenuMeta = async () => {
    const name = metaName.trim();

    if (!name) return alert("Name is required");

    setSavingMeta(true);

    try {
      const endpoint =
        metaModal === "department" ? "departments" : "categories";

      const res = await fetch(`${API}/menumeta/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        return alert(data.message || "Could not save");
      }

      if (metaModal === "department") {
        await fetchDepartments();
      } else {
        await fetchCategories();
      }

      setMetaName("");
      setMetaModal(null);
    } catch (error) {
      console.error(error);
      alert("Could not save");
    } finally {
      setSavingMeta(false);
    }
  };

  const handleRateChange = (branchId, value) => {
    const price = value === "" ? "" : Number(value);
    const existing = item.rates.find((rate) => rate.branch_id === branchId);

    const rates = existing
      ? item.rates.map((rate) =>
          rate.branch_id === branchId ? { ...rate, price } : rate,
        )
      : [...item.rates, { branch_id: branchId, price }];

    setItem({ ...item, rates });
  };

  const handleTaxChange = (index, field, value) => {
    const taxes = [...item.taxes];
    taxes[index] = {
      ...taxes[index],
      [field]: field === "percent" ? Number(value) : value,
    };
    setItem({ ...item, taxes });
  };

  const saveItem = async () => {
    if (!item.name.trim()) return alert("Item name required");
    if (!item.department_id) return alert("Select department");
    if (!item.category_id) return alert("Select category");

    const validRates = item.rates.filter(
      (rate) => rate.branch_id && rate.price !== "" && rate.price !== null,
    );

    if (!validRates.length) {
      return alert("Add a rate for at least one branch");
    }

    setSavingItem(true);

    try {
      const res = await fetch(isEdit ? `${API}/menu/${id}` : `${API}/menu/`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...item,
          name: item.name.trim(),
          rates: validRates,
        }),
      });

      const data = await res.json();

      if (data.status !== "success") {
        return alert(data.message || "Could not save menu item");
      }

      alert(isEdit ? "Item updated successfully" : "Item created successfully");
      navigate("/admindashboard/menu-master");
    } catch (error) {
      console.error(error);
      alert("Could not save menu item");
    } finally {
      setSavingItem(false);
    }
  };

  const input =
    "w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">
            {isEdit ? "Edit Menu Item" : "Add Menu Item"}
          </h1>

          <PermissionButton permissionModule="menu"
            type="button"
            onClick={() => navigate("/admindashboard/menu-master")}
            className="rounded-lg bg-white/10 px-4 py-2"
          >
            Back
          </PermissionButton>
        </div>

        {loadingItem ? (
          <div className="rounded-xl bg-[#0b0b0f] p-6 text-white/70">
            Loading menu item…
          </div>
        ) : (
          <>
            <div className="flex gap-3 border-b border-white/10 pb-2">
              {[
                ["basic", "Basic Info"],
                ["rates", "Branch Rates"],
                ["tax", "Tax"],
                ["desc", "Description"],
              ].map(([key, label]) => (
                <PermissionButton permissionModule="menu"
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 ${
                    tab === key ? "bg-amber-400 text-black" : "bg-white/5"
                  }`}
                >
                  {label}
                </PermissionButton>
              ))}
            </div>

            {tab === "basic" && (
              <div className="grid grid-cols-1 gap-4 rounded-xl bg-[#0b0b0f] p-6 md:grid-cols-2">
                <Field label="Item Code">
                  <input
                    className={input}
                    type="text"
                    placeholder="Enter item code"
                    value={item.item_code ?? ""}
                    onChange={(e) => setItem({ ...item, item_code: e.target.value })}
                  />
                </Field>

                <Field label="Item Name">
                  <input
                    className={input}
                    value={item.name}
                    onChange={(e) => setItem({ ...item, name: e.target.value })}
                  />
                </Field>

                <Field label="Finished Goods Inventory Item">
                  <select
                    className={input}
                    value={item.inventory_item_id}
                    onChange={(e) =>
                      setItem({
                        ...item,
                        inventory_item_id: e.target.value
                          ? Number(e.target.value)
                          : "",
                      })
                    }
                  >
                    <option value="">Select finished good</option>
                    {item.inventory_item_id &&
                      !inventoryItems.some((inventoryItem) =>
                        String(inventoryItem.id) === String(item.inventory_item_id),
                      ) && (
                        <option value={item.inventory_item_id} disabled>
                          Current link is unavailable as a finished good — choose another
                        </option>
                      )}
                    {inventoryItems.map((inventoryItem) => (
                      <option key={inventoryItem.id} value={inventoryItem.id}>
                        {inventoryItem.name}{inventoryItem.branch ? ` — ${inventoryItem.branch}` : ""}
                      </option>
                    ))}
                  </select>
                  {inventoryItems.length === 0 && (
                    <p className="mt-2 text-xs text-amber-400">
                      No finished goods found. Add an inventory item with type Finished Goods first.
                    </p>
                  )}
                </Field>

                <Field label="Department">
                  <div className="flex gap-2">
                    <select
                      className={input}
                      value={item.department_id}
                      onChange={(e) =>
                        setItem({
                          ...item,
                          department_id: e.target.value
                            ? Number(e.target.value)
                            : "",
                        })
                      }
                    >
                      <option value="">Select</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>

                    <PermissionButton permissionModule="menu"
                      type="button"
                      className="whitespace-nowrap rounded-lg bg-amber-400 px-3 text-black"
                      onClick={() => {
                        setMetaName("");
                        setMetaModal("department");
                      }}
                    >
                      + Add
                    </PermissionButton>
                  </div>
                </Field>

                <Field label="Category">
                  <div className="flex gap-2">
                    <select
                      className={input}
                      value={item.category_id}
                      onChange={(e) =>
                        setItem({
                          ...item,
                          category_id: e.target.value
                            ? Number(e.target.value)
                            : "",
                        })
                      }
                    >
                      <option value="">Select</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>

                    <PermissionButton permissionModule="menu"
                      type="button"
                      className="whitespace-nowrap rounded-lg bg-amber-400 px-3 text-black"
                      onClick={() => {
                        setMetaName("");
                        setMetaModal("category");
                      }}
                    >
                      + Add
                    </PermissionButton>
                  </div>
                </Field>

                <Field label="UOM">
                  <select
                    className={input}
                    value={item.uom}
                    onChange={(e) => setItem({ ...item, uom: e.target.value })}
                  >
                    {UOMS.map((uom) => (
                      <option key={uom} value={uom}>
                        {uom}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Veg Type">
                  <select
                    className={input}
                    value={item.veg_type}
                    onChange={(e) =>
                      setItem({ ...item, veg_type: e.target.value })
                    }
                  >
                    <option value="Veg">Veg</option>
                    <option value="Non-Veg">Non-Veg</option>
                    <option value="Egg">Egg</option>
                  </select>
                </Field>
              </div>
            )}

            {tab === "rates" && (
              <div className="grid grid-cols-1 gap-4 rounded-xl bg-[#0b0b0f] p-6 md:grid-cols-3">
                {branches.map((branch) => {
                  const rate = item.rates.find(
                    (row) => row.branch_id === branch.id,
                  );

                  return (
                    <Field key={branch.id} label={branch.name}>
                      <input
                        className={input}
                        type="number"
                        min="0"
                        step="0.01"
                        value={rate?.price ?? ""}
                        onChange={(e) =>
                          handleRateChange(branch.id, e.target.value)
                        }
                      />
                    </Field>
                  );
                })}
              </div>
            )}

            {tab === "tax" && (
              <div className="space-y-4 rounded-xl bg-[#0b0b0f] p-6">
                {item.taxes.map((tax, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                  >
                    <Field label="Tax Name">
                      <input
                        className={input}
                        value={tax.name}
                        onChange={(e) =>
                          handleTaxChange(index, "name", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Percentage">
                      <input
                        className={input}
                        type="number"
                        min="0"
                        step="0.01"
                        value={tax.percent}
                        onChange={(e) =>
                          handleTaxChange(index, "percent", e.target.value)
                        }
                      />
                    </Field>
                  </div>
                ))}
              </div>
            )}

            {tab === "desc" && (
              <div className="space-y-4 rounded-xl bg-[#0b0b0f] p-6">
                <Field label="Description">
                  <textarea
                    className={`${input} h-32`}
                    value={item.description}
                    onChange={(e) =>
                      setItem({ ...item, description: e.target.value })
                    }
                  />
                </Field>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={item.fast_moving}
                      onChange={(e) =>
                        setItem({
                          ...item,
                          fast_moving: e.target.checked,
                        })
                      }
                    />
                    Fast Moving
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={item.active}
                      onChange={(e) =>
                        setItem({
                          ...item,
                          active: e.target.checked,
                        })
                      }
                    />
                    Active
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <PermissionButton permissionModule="menu"
                type="button"
                onClick={saveItem}
                disabled={savingItem}
                className="rounded-xl bg-amber-400 px-8 py-3 text-black disabled:opacity-50"
              >
                {savingItem
                  ? "Saving..."
                  : isEdit
                    ? "Update Item"
                    : "Save Item"}
              </PermissionButton>
            </div>
          </>
        )}
      </div>

      {metaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#121217] p-6 shadow-2xl">
            <h2 className="text-xl font-semibold">
              Add {metaModal === "department" ? "Department" : "Category"}
            </h2>

            <input
              autoFocus
              className="mt-5 w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2 outline-none focus:border-amber-400"
              placeholder={`Enter ${metaModal} name`}
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveMenuMeta();
                if (e.key === "Escape") setMetaModal(null);
              }}
            />

            <div className="mt-6 flex justify-end gap-3">
              <PermissionButton permissionModule="menu"
                type="button"
                className="rounded-lg bg-white/10 px-4 py-2"
                onClick={() => setMetaModal(null)}
                disabled={savingMeta}
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="menu"
                type="button"
                className="rounded-lg bg-amber-400 px-4 py-2 font-medium text-black disabled:opacity-50"
                onClick={saveMenuMeta}
                disabled={savingMeta}
              >
                {savingMeta ? "Saving..." : "Save"}
              </PermissionButton>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-sm text-gray-400">{label}</div>
      {children}
    </div>
  );
}
